import {
  ContextMenuFlyout,
  EmbeddedButton,
  PreviewCard,
  styled,
  TableCell,
  TableRow,
  Tooltip,
} from '@zeiss/beyond-online-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import type { GetInvitedToProps, OnViewInvitationProps } from './';
import { DEFAULT_DATE_TIME_FORMAT } from 'src/constants';
import { SendInvitationModalData } from 'src/modules/Invitations/components/SendInvitationModal';
import { useInvitationClipboard } from 'src/modules/Invitations/hooks';
import {
  getStatusChip,
  isInvitationActive,
} from 'src/modules/Invitations/utils';
import type { GetInvitationResponse } from 'src/types/rest/managementPortalApi';

interface InvitationTableItemBaseProps {
  invitation: GetInvitationResponse;
  onSendInvitation: (payload: SendInvitationModalData) => void;
  onViewInvitation: (payload: OnViewInvitationProps) => void;
}

interface InvitationDesktopRowProps extends InvitationTableItemBaseProps {
  getInvitedTo: ({
    applicationName,
    tenantName,
  }: GetInvitedToProps) => JSX.Element;
}

const Container = styled.div`
  position: relative;
`;

const ActionContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-start;
`;

const StyledContextMenuFlyout = styled(ContextMenuFlyout)`
  position: absolute;
  top: var(--spacing-m);
  right: var(--spacing-m);
  div[role='dialog'] {
    width: max-content;
  }
`;

export const InvitationDesktopRow = ({
  getInvitedTo,
  invitation,
  onSendInvitation,
  onViewInvitation,
}: InvitationDesktopRowProps) => {
  const { t } = useTranslation(['common', 'invitations']);
  const { copyInvitationLink, textCopied } = useInvitationClipboard();

  const {
    applicationId,
    applicationName,
    code,
    email,
    invitationUrl,
    invitedBy,
    sentCount,
    status,
    statusLastModifiedAt,
    tenantId,
    tenantName,
    type,
  } = invitation;

  const canSendInvitation = isInvitationActive(status);

  const invitationSentCount = sentCount ?? 0;

  const lastModifiedDate = format(
    new Date(statusLastModifiedAt),
    DEFAULT_DATE_TIME_FORMAT,
  );

  const sendTooltipLabel =
    invitationSentCount >= 1
      ? t('invitations:action.resendInvitation')
      : t('invitations:action.sendInvitation');

  return (
    <TableRow>
      <TableCell>{code}</TableCell>
      <TableCell>{email}</TableCell>
      <TableCell>{getInvitedTo({ applicationName, tenantName })}</TableCell>
      <TableCell>{invitedBy}</TableCell>
      <TableCell>{getStatusChip({ status, t })}</TableCell>
      <TableCell>{lastModifiedDate}</TableCell>
      <TableCell>
        <ActionContainer>
          <Tooltip
            anchorEl={
              <EmbeddedButton
                aria-label={t('invitations:action.viewInvitation')}
                emphasis="high"
                icon="Visibility"
                onClick={() =>
                  onViewInvitation({ applicationId, code, tenantId, type })
                }
                size="m"
              />
            }
          >
            {t('invitations:action.viewInvitation')}
          </Tooltip>

          <Tooltip
            anchorEl={
              <EmbeddedButton
                aria-label={t('invitations:action.copyInvitationLink')}
                disabled={!canSendInvitation}
                emphasis="high"
                icon={textCopied ? 'Checkmark' : 'CopyContent'}
                onClick={() => copyInvitationLink(invitationUrl)}
                size="m"
              />
            }
          >
            {t('invitations:action.copyInvitationLink')}
          </Tooltip>

          <Tooltip
            anchorEl={
              <EmbeddedButton
                aria-label={sendTooltipLabel}
                disabled={!canSendInvitation}
                emphasis="high"
                icon="SendMessage"
                onClick={() => onSendInvitation({ code, email, sentCount })}
                size="m"
              />
            }
          >
            {sendTooltipLabel}
          </Tooltip>
        </ActionContainer>
      </TableCell>
    </TableRow>
  );
};

export const InvitationMobileCard = ({
  invitation,
  onSendInvitation,
  onViewInvitation,
}: InvitationTableItemBaseProps) => {
  const { t } = useTranslation(['common', 'invitations']);
  const { copyInvitationLink, textCopied } = useInvitationClipboard();

  const {
    applicationId,
    applicationName,
    code,
    email,
    invitationUrl,
    invitedBy,
    sentCount,
    status,
    statusLastModifiedAt,
    tenantId,
    tenantName,
    type,
  } = invitation;

  const canSendInvitation = isInvitationActive(status);

  const lastModifiedDate = format(
    new Date(statusLastModifiedAt),
    DEFAULT_DATE_TIME_FORMAT,
  );

  return (
    <Container>
      <StyledContextMenuFlyout
        enforcePosition
        groups={[
          [
            ...(canSendInvitation
              ? [
                  {
                    icon: textCopied
                      ? ('Checkmark' as const)
                      : ('CopyContent' as const),
                    label: t('invitations:action.copyInvitationLink'),
                    onClick: () => copyInvitationLink(invitationUrl),
                  },
                ]
              : []),
            {
              icon: 'Visibility',
              label: t('invitations:action.viewInvitation'),
              onClick: () =>
                onViewInvitation({ applicationId, code, tenantId, type }),
            },
            ...(canSendInvitation
              ? [
                  {
                    icon: 'SendMessage' as const,
                    label:
                      sentCount >= 1
                        ? t('invitations:action.resendInvitation')
                        : t('invitations:action.sendInvitation'),
                    onClick: () => onSendInvitation({ code, email, sentCount }),
                  },
                ]
              : []),
          ],
        ]}
        position="left"
      />
      <PreviewCard
        chips={getStatusChip({ status, t })}
        eyebrow={t('common:glossary.inviteCode')}
        headline={code}
        properties={[
          {
            key: t('common:glossary.email'),
            value: email,
          },
          ...(applicationName
            ? [
                {
                  key: t('common:table.header.invitedTo'),
                  value: `${applicationName} (${t('common:glossary.application')})`,
                },
              ]
            : []),
          ...(tenantName
            ? [
                {
                  key: t('common:table.header.invitedTo'),
                  value: `${tenantName} (${t('common:glossary.tenant')})`,
                },
              ]
            : []),
          {
            key: t('common:table.header.invitedBy'),
            value: invitedBy,
          },
          {
            key: t('common:table.header.lastUpdated'),
            value: lastModifiedDate,
          },
        ]}
      />
    </Container>
  );
};
